import React from 'react';
import { Card, CardContent } from '@/components/ui/card';

import { Lock } from 'lucide-react';

import { useSelector } from 'react-redux';
import { selectClients } from '@client/model';

import { cn } from '@/lib/utils';
import { isNullOrUndefined } from '@shared';

interface PartnerApplicationCardProps {
  application: ApplicationCardData;
  partnerClientId: string;
}

function PolicyApplicationDashboardPartnerCard({
  application,
  partnerClientId,
}: PartnerApplicationCardProps) {
  const clients = useSelector(selectClients);
  const client = clients.find(c => c.clientId === partnerClientId);
  const clientName = `${client?.clientFirstName || 'Primary'} ${client?.clientLastName || 'Insured'}`;

  function getStatusConfig(status: ApplicationStatus) {
    switch (status) {
      case 'not_created':
      case 'not_started':
        return {
          label: 'Not Started',
          variant: 'secondary' as const,
          color: 'text-gray-700',
          bgColor: 'bg-gray-100',
          icon: <div className="h-4 w-4 rounded-full border-2 border-gray-300" />,
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          variant: 'default' as const,
          color: 'text-primary',
          bgColor: 'bg-primary-50',
          icon: <div className="border-primary bg-primary-100 h-4 w-4 rounded-full border-2" />,
        };
      case 'completed':
      case 'submitted':
        return {
          label: 'Submitted',
          variant: 'default' as const,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          icon: (
            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-green-500">
              <div className="h-2 w-2 rounded-full bg-white" />
            </div>
          ),
        };
      default:
        return {
          label: status,
          variant: 'secondary' as const,
          color: 'text-gray-700',
          bgColor: 'bg-gray-100',
          icon: <div className="h-4 w-4 rounded-full border-2 border-gray-300" />,
        };
    }
  }

  const statusConfig = React.useMemo(
    () => getStatusConfig(application.status),
    [application.status],
  );

  return (
    <Card className="border-primary-200 rounded-2xl border">
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-primary-dark text-xl font-medium">{application.title}</h3>
              <p className="text-base text-gray-700">by {application.carrier}</p>
            </div>
            <div className="flex items-center gap-2">
              {statusConfig.icon}
              <span className={cn('text-sm font-medium', statusConfig.color)}>
                {statusConfig.label}
              </span>
              {application.progress && application.status === 'in_progress' ? (
                <span className="text-sm text-gray-700">{application.progress}%</span>
              ) : null}
            </div>
          </div>

          {application.status === 'in_progress' && !isNullOrUndefined(application.progress) && (
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${application.progress}%` }}
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 text-end text-sm italic text-gray-700">
            <Lock className="size-4" />
            Only <span className="text-primary">{clientName}</span> can resume this application.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PolicyApplicationDashboardPartnerCard;
